import * as React from 'react';
import {View, StyleSheet, Platform} from 'react-native';
import {DeviceSize} from '../../../shared/theme/helpers';
import {Field} from 'redux-form';
import {Button} from '../../../shared/components/button';
import {BaseField} from '../../../shared/components/baseField';
import {useStyles} from '../../../shared/hooks/styles';
import {images} from '../../../assets/icons';
import {defaults} from '../../../shared/constants/defaults';
import {LoginType} from '../../../shared/models/api';
import {Form} from '../../../shared/components/form';
import {useTheme} from '../../../shared/theme';
import {QrCodeIcon} from '../components/qrCodeIcon';
import {formatIdentificator, formatLoginLabel} from '../formatters';
import {OrDelimiter} from '../../../shared/components/orDelimiter';
import {useIsSmallScreenDevice} from '../../../shared/hooks/useIsSmallScreenDevice';

export interface Props {
  onSubmit: (e?: any) => void;
  needCard?: boolean;
  loginType?: LoginType;
  onAutoFill: (fieldName: string) => (data: any) => void;
  submitting: boolean;
  onEsiaClick: (e?: any) => void;
}

export default (props: Props) => {
  const {onSubmit, onEsiaClick, needCard, loginType, onAutoFill, submitting} = props;
  const theme = useTheme();

  const identificatorFormatter = React.useMemo(() => {
    if (!loginType && Platform.OS === 'web') {
      return undefined;
    } else {
      return (v: any) => formatIdentificator(v, loginType);
    }
  }, [loginType]);

  const identificatorNormalize = (value: any) => {
    const res = (value as string)?.replace(/ /g, '').substring(0, defaults.maxLoginLength);
    return res;
  };

  const isSmallScreen = useIsSmallScreenDevice();

  const styles = useStyles(({mediaOnly, platformOnly}) => {
    return {
      innerButton: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        color: theme.color.primaryColor,
      },
      textInnerContainer: theme.fonts.bodySmall,
      container: StyleSheet.flatten([
        {
          flexGrow: 1,
          flexShrink: 0,
        },
        mediaOnly(
          DeviceSize.desktop,
          {width: 300, marginTop: 24},
          {width: Platform.OS === 'web' ? 260 : 284, paddingTop: 18},
        ),
      ]),
      fieldContainer: {
        ...mediaOnly(DeviceSize.desktop, {marginBottom: 50}, {marginBottom: 40, marginTop: 5}),
      },
      buttonWrapper: {
        marginTop: 'auto',
      },
      button: StyleSheet.flatten([
        {
          alignSelf: 'center',
          marginTop: 'auto',
          marginBottom: 0,
        },
        mediaOnly(DeviceSize.desktop, {width: 300}, {width: '100%'}),
      ]),
      cardFieldStyle: platformOnly('web', {marginTop: 24}, {}),
    };
  });

  return (
    <Form style={styles.container} onSubmit={onSubmit}>
      <View style={styles.fieldContainer}>
        <Field
          component={BaseField}
          label={formatLoginLabel(loginType)}
          type='digits'
          name='identificator'
          required
          disabled={needCard || submitting}
          onSubmitEditing={onSubmit}
          autoFocus={false}
          format={identificatorFormatter}
          normalize={identificatorNormalize}
          props={{
            right: Platform.OS !== 'web' && !needCard && (
              <QrCodeIcon onScan={onAutoFill('identificator')} adjustFor='input' />
            ),
          }}
        />
        {needCard ? (
          <Field
            autoFocus={false}
            component={BaseField}
            label='Номер карты'
            type='digits'
            name='cardNumber'
            required
            style={styles.cardFieldStyle as any}
            normalize={identificatorNormalize}
            onSubmitEditing={onSubmit}
            props={{
              right: Platform.OS !== 'web' && <QrCodeIcon onScan={onAutoFill('cardNumber')} adjustFor='input' />,
            }}
            disabled={submitting}
          />
        ) : null}
      </View>
      <View style={styles.buttonWrapper}>
        <Button
          id='login-enter-next'
          type='secondary'
          style={styles.button}
          onPress={onSubmit}
          iconProps={{width: 16, height: 19}}
          icon={images.Right}
          text='Далее'
          disabled={submitting}
        />
        <OrDelimiter marginBottom={isSmallScreen ? 20 : undefined} marginTop={isSmallScreen ? 20 : undefined} />
        <Button
          id='login-width-esia'
          type='secondary'
          style={styles.button}
          onPress={onEsiaClick}
          iconProps={{width: 20, height: 21}}
          icon={images.Esia}
          iconPosition='left'
          text='Войти через ЕСИА'
          disabled={submitting}
        />
      </View>
    </Form>
  );
};
